import { Link } from 'react-router-dom';
import type {
  BoardConfig,
  EventPost,
  NoticePost,
  Post,
  UpdatePost,
} from '../types/board.type.ts';
import {
  EVENT_STATUS_CLASS,
  EVENT_STATUS_LABEL,
  formatBoardDate,
  formatPeriod,
  resolveEventStatus,
} from './utils.ts';
import { NOTICE_CATEGORY_LABEL } from '../constants/labels.ts';
import { CATEGORY_CHIP, CATEGORY_CHIP_NEUTRAL } from '../components/chipStyles.ts';

interface BoardItemProps {
  config: BoardConfig<Post>;
  post: Post;
}

/** 행에 보여줄 확장자 배지 수 — 넘치면 개수만 남는다 */
const MAX_EXT_BADGES = 3;

// 타입 가드
const isEvent = (p: Post): p is EventPost => 'startsAt' in p && 'endsAt' in p;
const isUpdate = (p: Post): p is UpdatePost => 'productSlug' in p;
const isNotice = (p: Post): p is NoticePost =>
  !isEvent(p) && !isUpdate(p);

/** 칩 라벨 — 하위 분류가 있으면 하위 분류, 없으면 분류 (FAQ 와 같은 규칙) */
function getCategoryLabel(config: BoardConfig<Post>, post: Post) {
  const { rowFields } = config;
  if (rowFields.showProduct && isUpdate(post)) {
    return (
      config.categories?.find((c) => c.value === post.productSlug)?.label ??
      post.productSlug.toUpperCase().replace('-', ' ')
    );
  }
  if (!isNotice(post)) return null;

  if (rowFields.showSubCategory && 'subCategory' in post && post.subCategory) {
    const category = 'category' in post ? post.category : null;
    const sub = config
      .getSubCategoryOptions?.(category)
      .find((o) => o.value === post.subCategory)?.label;
    if (sub) return sub;
  }
  if (rowFields.showCategory && post.category) {
    return (
      config.categories?.find((c) => c.value === post.category)?.label ??
      NOTICE_CATEGORY_LABEL[post.category as keyof typeof NOTICE_CATEGORY_LABEL]
    );
  }
  return null;
}

/**
 * 단일 게시글 행. FAQ 목록과 같은 한 줄 모양이다.
 * 분류 칩 — 제목 — 우측 메타(첨부/기간/날짜/조회수) 구조.
 *
 * config.rowFields 로 표시 항목을 결정해서 게시판마다 보여줄 필드를 달리한다.
 */
export function BoardItem({ config, post }: BoardItemProps) {
  const { rowFields } = config;
  const eventStatus = isEvent(post) ? resolveEventStatus(post) : null;
  const categoryLabel = getCategoryLabel(config, post);

  return (
    <Link
      to={config.detailPath(post.id)}
      className="group flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-black/[0.06] py-5 dark:border-white/10"
    >
      {/* 썸네일 (이벤트) — 한 줄 높이에 맞춘 작은 크기 */}
      {rowFields.showThumbnail && (
        <div className="h-12 w-20 shrink-0 overflow-hidden rounded-lg bg-neutral-100 dark:bg-dark-card">
          {post.thumbnailUrl ? (
            <img
              src={post.thumbnailUrl}
              alt=""
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-neutral-200 to-neutral-100" />
          )}
        </div>
      )}

      {/* 고정 */}
      {post.isPinned && (
        <span className="text-[13px] font-bold text-[#FA0F00]">고정</span>
      )}

      {/* 이벤트 상태 */}
      {eventStatus && (
        <span className={`${CATEGORY_CHIP} font-bold ${EVENT_STATUS_CLASS[eventStatus]}`}>
          {EVENT_STATUS_LABEL[eventStatus]}
        </span>
      )}

      {/* 분류 (공지·자료실 하위 분류·업데이트 제품) */}
      {categoryLabel && (
        <span className={`${CATEGORY_CHIP} ${CATEGORY_CHIP_NEUTRAL} -mr-2`}>
          {categoryLabel}
        </span>
      )}

      {/* 업데이트 버전 */}
      {rowFields.showVersion && isUpdate(post) && post.version && (
        <span className="-mr-2 shrink-0 font-mono text-[12px] font-bold text-neutral-950/45 dark:text-white/45">
          v{post.version}
        </span>
      )}

      <h3 className="min-w-0 flex-1 basis-[12rem] text-[16px] font-semibold text-neutral-950 transition-colors group-hover:text-primary dark:text-neutral-100">
        {post.title}
      </h3>

      {/* 우측 메타 */}
      <div className="ml-auto flex shrink-0 items-center gap-3 text-[13px] text-neutral-950/45 dark:text-white/45">
        {/* 첨부 형식·개수 (자료실) — 상세 첨부 목록과 같은 확장자 배지 */}
        {rowFields.showAttachments && post.attachmentSummary && (
          <span
            className="inline-flex items-center gap-1"
            aria-label={`첨부 ${post.attachmentSummary.count}개`}
          >
            {post.attachmentSummary.extensions
              .slice(0, MAX_EXT_BADGES)
              .map((ext) => (
                <span
                  key={ext}
                  className="rounded bg-neutral-950/[0.06] dark:bg-white/10 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-neutral-600 dark:text-neutral-300"
                >
                  {ext}
                </span>
              ))}
            {post.attachmentSummary.count > 1 && (
              <span className="text-[11px] font-semibold">
                {post.attachmentSummary.count}개
              </span>
            )}
          </span>
        )}

        {/* 이벤트 기간 */}
        {rowFields.showPeriod && isEvent(post) && (
          <span className="font-mono text-[12px]">
            {formatPeriod(post.startsAt, post.endsAt)}
          </span>
        )}

        <span>{formatBoardDate(post.publishedAt)}</span>

        {rowFields.showViewCount && post.viewCount !== undefined && (
          <span>조회 {post.viewCount.toLocaleString('ko-KR')}</span>
        )}
      </div>
    </Link>
  );
}
